import { reactive } from 'vue'
import {
  httpGetJson,
  httpPostJson,
  httpDeleteJson,
  httpPatchJson,
} from '@/lib/net'
import markupService from '@/services/markup-service'
import marketingEventService from '@/services/marketing-event-service'
import { getUserId } from '@/services/identity-client'
import { log } from '@/services/logging-service'
import { PERMISSIONS } from '@/lib/content-collections/enums-content-collections'

const state = reactive({
  allGrants: {
    toCurrentUser: undefined,
    fromCurrentUser: undefined,
  },
  shouldFetchNewGrants: true,
})

/**
 * Request the share grants for an entity. Typically used for shared with display.
 *
 * @param {string} id - ID of entity
 * @param {string} type - Type of entity (eg. markup or content_collection)
 */
export const getShareGrantsForEntity = async (id, type) => {
  try {
    const grants = await httpGetJson(
      `/v1/sharing/grants?entity_type=${type}&entity_id=${id}`
    )
    return grants?.data || []
  } catch (e) {
    log('get_share_grants_for_entity_failed', {
      error: e,
      markup_id: id,
    })
  }
}

/**
 * Request all existing share grants for an entity regardless of whether or not it includes the current user.
 * Typically used for shared with display.
 *
 * @param {string} id - ID of entity
 * @param {string} type - Type of entity (eg. markup or content_collection)
 */
export const getAllShareGrantsForEntity = async (id, type) => {
  try {
    const grants = await httpGetJson(
      `/v1/sharing/grants?entity_type=${type}&entity_id=${id}&include_all=true`
    )
    return grants?.data || []
  } catch (e) {
    log('get_all_share_grants_for_entity_failed', {
      error: e,
      markup_id: id,
    })
  }
}

/**
 * Request all existing share grants
 *
 * @return {{toCurrentUser?: Map<string, object[]>, fromCurrentUser?: Map<string, object[]>}}
 */
export const getAllShareGrants = () => {
  if (state.shouldFetchNewGrants) {
    state.shouldFetchNewGrants = false
    fetchAllShareGrants()
  }

  return state.allGrants
}

/**
 * *(AI-generated)*
 * Marks the share grants as stale so they will be refetched on the next request.
 */
export const setRefetchShareGrants = () => {
  state.shouldFetchNewGrants = true
  state.allGrants = {
    toCurrentUser: new Map(),
    fromCurrentUser: new Map(),
  }
}

/**
 * Request the share grants for an entity. Typically used for shared with display.
 *
 * @param {string} id - ID of entity
 * @param {string} type - Type of entity (eg. markup or content_collection)
 */
const fetchAllShareGrants = async () => {
  performance.mark('time_for_share_grants_load_started')
  try {
    const grants = await httpGetJson(`/v1/sharing/grants`)
    const currentUserId = await getUserId()

    state.allGrants.toCurrentUser = new Map()
    state.allGrants.fromCurrentUser = new Map()

    grants.data.forEach((grant) => {
      if (grant.to.id === currentUserId) {
        if (!state.allGrants.toCurrentUser?.has(grant.entity.id)) {
          state.allGrants.toCurrentUser?.set(grant.entity.id, [])
        }

        const currentArr = state.allGrants.toCurrentUser?.get(grant.entity.id)
        currentArr.push(grant)
        state.allGrants.toCurrentUser?.set(grant.entity.id, currentArr)
      } else if (grant.from === currentUserId) {
        if (!state.allGrants.fromCurrentUser?.has(grant.entity.id)) {
          state.allGrants.fromCurrentUser?.set(grant.entity.id, [])
        }

        const currentArr = state.allGrants.fromCurrentUser?.get(grant.entity.id)
        currentArr.push(grant)
        state.allGrants.fromCurrentUser?.set(grant.entity.id, currentArr)
      }
    })
  } catch (e) {
    log('get_all_share_grants_failed', {
      error: e,
    })
  }
  performance.mark('time_for_share_grants_load_finished')

  performance.measure(
    'time_for_share_grants_load',
    'time_for_share_grants_load_started',
    'time_for_share_grants_load_finished'
  )
}

/**
 * Patch update the grant's role type for a given collection
 */
export const updateGrantRoleTypeForCollection = async (collectionId, role) => {
  try {
    const body = {
      role,
    }

    const response = await httpPatchJson(
      `/v1/sharing/grants?entity_type=collection&entity_id=${collectionId}`,
      body
    )
    return response?.data
  } catch (e) {
    log('update_grant_role_failed', {
      error: e,
      id: collectionId,
      role: role,
    })
  }
}

/**
 * Patch update the grant's role type for a specific user for a given collection
 */
export const updateIndividualGrantRoleTypeForCollection = async (
  collectionId,
  role,
  userIds
) => {
  try {
    const grants = []
    userIds.forEach((id) => {
      const grantInfo = {
        subject: {
          id: id,
          type: 'user',
        },
        role: role,
      }

      grants.push(grantInfo)
    })

    const body = {
      grants: grants,
    }

    const response = await httpPatchJson(
      `/v1/sharing/grants?entity_type=collection&entity_id=${collectionId}`,
      body
    )
    return response?.data
  } catch (e) {
    log('update_grant_role_failed', {
      error: e,
      id: collectionId,
      role: role,
    })
  }
}

/**
 * Create a share grant by its share ID
 *
 * @param {string} id - ID of the share we would like to retrieve from the service
 * return {Promise<object>} Share info
 */
export const createShareGrants = async (id) => {
  try {
    const share = await httpPostJson(`/v1/sharing/grants`, {
      share_id: id,
    })
    const data = share?.data
    if (!data) {
      return null
    }
    data.created_at = data.created_at ? new Date(data.created_at) : null
    data.updated_at = data.updated_at ? new Date(data.updated_at) : null
    return data
  } catch (e) {
    if (
      e?.error?.status === '409' &&
      e?.error?.title === 'requesting user created this share'
    ) {
      return e
    }

    log('get_share_failed', {
      error: e,
      share_id: id,
    })
  }
}

/**
 * Lookup a share by ID with expanded entities. Returns full share data
 * including role and entities.
 *
 * @returns {import('@/lib/share-utils').ExpandedEntitiesResponse['data']|null}
 */
export const getShareWithEntities = async (id) => {
  try {
    const share = await httpGetJson(`/v1/sharing/shares/${id}?expand=entities`)
    if (!share?.data) {
      return null
    }
    return share.data
  } catch (e) {
    log('get_share_with_entities_failed', {
      error: e,
      share_id: id,
    })
  }
}

const getLinkRole = () => {
  return PERMISSIONS.VIEW_ONLY
}

/**
 * Get a share link (branch link)
 *
 * @param {string} type - Type of share link requested (markup or collection)
 * @param {array} itemsToShare - Items to share with this link
 * @return {string} - Share link (branch)
 */
export const getShareLink = async (
  type,
  itemsToShare,
  linkRole = undefined,
  skipEvent = false
) => {
  const entities = _serializeEntities(type, itemsToShare)

  const body = {
    entities,
    link: {
      role: linkRole ?? getLinkRole(type),
    },
  }

  if (type === 'collection') {
    // count the items in the collecton
    _setShareCounts(itemsToShare[0]?.entities)
  } else {
    // count the markups + routes
    _setShareCounts(itemsToShare)
  }

  try {
    const url = '/v1/sharing/shares?link=webmap'

    const response = await httpPostJson(url, body)

    let contributorPermission = response?.data?.link?.role ?? ''

    if (contributorPermission === PERMISSIONS.VIEW_ONLY) {
      contributorPermission = 'viewer'
    }

    if (!skipEvent) {
      let entityType

      switch (type) {
        case 'collection':
          entityType = 'content collection'
          break
        default:
          entityType = 'markup'
          break
      }

      marketingEventService.publish('share_link_requested', {
        entity_type: entityType,
        share_uuid: response?.data?.id,
        contributor_permission: contributorPermission,
      })
    }

    if (response?.data?.link?.url) {
      return response.data
    }
  } catch (e) {
    log('create_share_link_error', {
      error: e,
      shareType: type,
      markupsIds:
        type !== 'collection' ? itemsToShare.map((m) => m.uuid) : null,
      collectionId: type === 'collection' ? itemsToShare[0].uuid : null,
    })
    return { link: { url: 'Error loading share link for items' } }
  }
}

/**
 * Updates an existing share link to have a new role type
 *
 * @param {string} type - Type of share link requested (markup or collection)
 * @param {array} itemsToShare - Items to share with this link
 * @return {string} - Share link (branch)
 */
export const updateShareLink = async (entityId, entityType, linkRole) => {
  const body = {
    link: {
      role: linkRole,
    },
  }

  try {
    const url = `/v1/sharing/shares?entity_type=${entityType}&entity_id=${entityId}`
    const response = await httpPatchJson(url, body)

    if (response?.data?.link?.url) {
      return response.data
    }
  } catch (e) {
    log('update_share_link_error', {
      error: e,
    })
    return 'Error loading share link for items'
  }
}

/**
 * *(AI-generated)*
 * Retrieves an existing share link for a given entity by its ID and type.
 */
export const getShareLinkByEntityId = async (
  type,
  entityId,
  skipEvent = false
) => {
  try {
    const response = await httpGetJson(
      `/v1/sharing/shares?entity_type=${type}&entity_id=${entityId}`
    )

    if (!skipEvent && response?.data) {
      let contributorPermission = response.data[0]?.link?.role ?? ''

      if (contributorPermission === PERMISSIONS.VIEW_ONLY) {
        contributorPermission = 'viewer'
      }

      marketingEventService.publish('share_link_requested', {
        entity_type: type === 'collection' ? 'content collection' : 'markup',
        share_uuid: response.data[0]?.id,
        contributor_permission: contributorPermission,
      })
    }
    return response.data
  } catch (e) {
    return 'Error loading share link for items'
  }
}

/**
 * Remove share grant for a single entity
 *
 * @param {string} id - ID of entity to remove grant for
 * @param {string} type - Type of entity
 * @param {string} accountId - Account ID to remove the grant for
 */
export const removeShareGrantForEntity = async (id, type, accountId) => {
  try {
    await httpDeleteJson(
      `/v1/sharing/grants?entity_type=${type}&include_shares=false&entity_id=${id}&to_type=user&to_id=${accountId}`
    )
    marketingEventService.publish('unshare_account_clicked')
  } catch (e) {
    log('remove_grant_failed', {
      error: e,
      entity_id: id,
      grant_id: accountId,
    })
    throw e
  }
}

/**
 * Stop all sharing for a given markup
 *
 * @param {string} id - ID of entity to remove grants for
 * @param {string} type - Type of entity
 */
export const removeAllShareGrantsForEntity = async (id, type) => {
  try {
    await httpDeleteJson(
      `/v1/sharing/grants?entity_type=${type}&include_shares=true&entity_id=${id}`
    )
    marketingEventService.publish('unshare_all_clicked')
  } catch (e) {
    log('unshare_all_failed', {
      error: e,
      entity_id: id,
    })
    throw e
  }
}

const _serializeEntities = (type, itemsToShare) => {
  switch (type) {
    case 'markup':
      return itemsToShare.map((markup) => {
        return {
          type: markup.type === 'route' ? markup.type : 'markup',
          id: markup.uuid,
        }
      })
    case 'route':
      return itemsToShare.map((markup) => {
        return {
          type,
          id: markup.uuid,
        }
      })

    case 'collection':
      return [
        {
          type,
          id: itemsToShare[0].uuid,
        },
      ]
    default:
      throw new Error(`Invalid shareType: "${type}"`)
  }
}

/**
 * *(AI-generated)*
 * Counts the markup types in itemsToShare and sets the share event values.
 */
export const _setShareCounts = (itemsToShare) => {
  const counts = {
    markup_count: 0,
    waypoint_count: 0,
    line_count: 0,
    area_count: 0,
    track_count: 0,
    photo_count: 0,
    route_count: 0,
  }

  itemsToShare.forEach((lookup) => {
    // get the markup from the markup service since folders have slim entities
    const item = markupService.getMarkupByID(lookup.uuid)
    if (item?.photos?.length > 0) {
      counts.photo_count += item.photos.length
    }

    counts.markup_count++
    switch (item?.type) {
      case 'waypoint':
        counts.waypoint_count++
        break
      case 'line':
        counts.line_count++
        break
      case 'shape':
        counts.area_count++
        break
      case 'track':
        counts.track_count++
        break
      case 'route':
        counts.route_count++
        break
    }
  })

  marketingEventService.setEventValues('share_link_requested', counts)
}
